import {
  Box,
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  SidebarPane,
  SidebarPaneBody,
  SidebarPaneFooter,
  SidebarPaneHeader,
  SidebarPaneHeaderReveal,
  SidebarPaneHeaderLabel,
  SidebarPaneHeaderTrigger,
  SidebarPaneTitle,
  SidebarSeparator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type IconName,
} from "@salestrak/ui";
import * as React from "react";

import {
  CustomizeNavigationDialog,
  type CustomizeNavigationTab,
} from "#app/components/navigation/module-rail/customize-navigation-dialog";
import { CollapseSidebarButton } from "#app/components/navigation/collapse-sidebar-button";
import { GlobalSearchButton } from "#app/components/global-search/global-search-button";
import { useSidebarResize } from "#app/components/navigation/use-sidebar-resize";
import type { Omittable } from "@salestrak/ui";

/**
 * ModuleMenu — the module menus that fill the sidebar pane (Home, Workflows,
 * Teams). The pane shell, its gutter and its scroll behaviour are the
 * library's `SidebarPane`; this fills its three slots with domain content:
 *
 *  - Header: module-menu title (a menu trigger when the instance passes
 *    `titleMenu`), a collapse control (double-left chevron) revealed on
 *    header hover, and the always-visible search + create pair on the
 *    trailing edge.
 *  - Body: the instance-defined, ordered section list, separated by dividers.
 *  - Footer (opt-in via `customizeNav`): a pinned Customize Navigation
 *    button, revealed on pane hover like the collapse control.
 *
 * Each module-menu instance defines its own `sections` array. Section `id`s
 * key the user's section-order preference — instances must keep them stable.
 */

export type ModuleMenuSection = {
  /** Stable id — future user preferences key order/visibility on this. */
  id: string;
  /** Section body, rendered in array order. */
  Component: React.ComponentType;
};

export type ModuleMenuOption = Omittable<{
  id: string;
  label: string;
  icon?: IconName;
  onClick: () => void;
}>;

type ModuleMenuProps = Omittable<{
  title: string;
  /** Ordered section list, defined by the module-menu instance. */
  sections: ModuleMenuSection[];
  /** Options for the header's context menu. Omit to hide the trigger. */
  options?: ModuleMenuOption[];
  /** Rich create popover (trigger included) rendered in place of the plain
   * options menu — the Home/Workflows `CreateMenuPopover`. Wins over
   * `options` when both are set. */
  createMenu?: React.ReactNode;
  /** Module-scope menu popup (a `MenuPopup`, items included) opened from the
   * pane title itself, which renders as a caret-bearing
   * `SidebarPaneHeaderTrigger` instead of plain text — the Workflows
   * `WorkflowSettingsMenuPopup`. Lives on the title rather than as a header
   * action so the hover-revealed cluster stays one control wide and never
   * crowds the title. Omit for a plain title (the default). */
  titleMenu?: React.ReactNode;
  /** Opt-in pinned footer button opening the Customize Navigation dialog at
   * the given tab. Omit to hide (the default). */
  customizeNav?: { initialTab: CustomizeNavigationTab };
  /** Opt-in pinned footer content (e.g. the AI menu's usage stats), seated
   * under a closing hairline, outside the scroll region. Renders above the
   * `customizeNav` button when both are set. */
  footer?: React.ReactNode;
}>;

export function ModuleMenu({
  title,
  sections,
  options,
  createMenu,
  titleMenu,
  customizeNav,
  footer,
}: ModuleMenuProps) {
  const sidebarResize = useSidebarResize();
  const isCompact = useIsMobile();
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  return (
    <SidebarPane
      // The pane drags its own right edge.
      {...sidebarResize}
    >
      <SidebarPaneHeader
        actions={
          <>
            {/* Hover-revealed chrome yields its width at rest so the title
              * gets the whole row; on hover it slides in and the title
              * re-truncates. The cluster holds ONLY the collapse control —
              * module-scope actions ride the title menu, so the expanded
              * cluster never grows wide enough to crowd the title. The create
              * control stays outside — it is the header's always-visible
              * primary action. */}
            <SidebarPaneHeaderReveal>
              <CollapseSidebarButton />
            </SidebarPaneHeaderReveal>
            {/* Search is the header's other always-visible action, pinned to
              * the create control's left. Outside the reveal cluster on
              * purpose: global search is reachable from every module, so it
              * must not depend on hovering the header to appear. On compact
              * the drawer's own header carries the ONE search — a second
              * glyph here would offer the same action twice on one surface. */}
            {!isCompact && <GlobalSearchButton />}
            {createMenu}
            {createMenu == null && options != null && options.length > 0 && (
              <Menu>
                {/* The shadow rides a pill wrapper matching the Button:
                  * the outline variant manages its own shadow per state, so a
                  * shadow on the button itself would be clobbered on press.
                  * Flex, not block — inline layout adds descender space below
                  * the button and the shadow pokes out under it. */}
                <Box direction="row" rounded="full" shadow="control">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <MenuTrigger
                          render={
                            <Button
                              aria-label={`${title} actions`}
                              size="sm"
                              variant="outline"
                              // Light: no border — the `control` shadow (which
                              // carries a 1px hairline ring, see
                              // --control-shadow) gives the edge instead. Dark
                              // keeps its already-visible border.
                              className="not-dark:border-transparent!"
                            />
                          }
                        >
                          {/* The `+` is this control's primary action — opt it
                            * out of Button's `opacity-80` glyph dim so it reads
                            * crisp; the chevron stays muted as the secondary
                            * dropdown affordance. */}
                          <Icon name="plus" size="sm" className="opacity-100" />
                          <Icon color="muted" name="chevron-down" size="sm" />
                        </MenuTrigger>
                      }
                    />
                    <TooltipPopup side="top">Create</TooltipPopup>
                  </Tooltip>
                </Box>
                {/* Hugs the trigger — its top-left corner sits on the
                  * trigger's bottom-left corner, so it opens rightward into
                  * the page rather than back over the sidebar (the same
                  * alignment CreateMenuPopover takes) — on the NARROW
                  * `action` rung: this menu holds a couple of short verbs,
                  * and the 280px `panel` rung is for menus carrying
                  * descriptions or keycaps. `size="lg"` rows put it on the
                  * same 32px rhythm (glyph at its natural inset, label on a
                  * 42px edge) the CreateMenuPopover's rows read at, so the
                  * two create surfaces are one grammar at different widths. */}
                <MenuPopup align="start" side="bottom" width="action">
                  {options.map((option, index) => (
                    <MenuItem
                      key={`${option.id}-${index}`}
                      onClick={option.onClick}
                      size="lg"
                    >
                      {option.icon != null && (
                        <Icon color="muted" name={option.icon} size="md" />
                      )}
                      <Text>{option.label}</Text>
                    </MenuItem>
                  ))}
                </MenuPopup>
              </Menu>
            )}
          </>
        }
      >
        {titleMenu != null ? (
          <Menu>
            {/* The title IS the menu trigger — a ghost identity trigger on
              * the title's reading edge, caret as the affordance. Reachable
              * without hovering anything (and on touch), unlike the reveal
              * cluster it replaced. */}
            <MenuTrigger render={<SidebarPaneHeaderTrigger />}>
              <SidebarPaneHeaderLabel>{title}</SidebarPaneHeaderLabel>
              <Icon color="muted" name="chevron-down" size="sm" />
            </MenuTrigger>
            {titleMenu}
          </Menu>
        ) : (
          <SidebarPaneTitle>{title}</SidebarPaneTitle>
        )}
      </SidebarPaneHeader>

      <SidebarPaneBody label={title}>
        {sections.map((section, index) => (
          <React.Fragment key={`${section.id}-${index}`}>
            {index > 0 && <SidebarSeparator />}
            <section.Component />
          </React.Fragment>
        ))}
      </SidebarPaneBody>

      {footer != null && (
        <>
          {/* The hairline closes the scroll region so the pinned stats read
            * as chrome, not the list's last row. */}
          <SidebarSeparator />
          <SidebarPaneFooter>{footer}</SidebarPaneFooter>
        </>
      )}

      {customizeNav != null && (
        <>
          <SidebarPaneFooter>
            <Box
              fill="width"
              hoverReveal="pane"
              revealOnTouch
              render={
                // Desktop keeps the quiet secondary chip (a hover reveal on
                // the white page). The phone drawer paints the `sidebar`
                // gray, where light mode's `secondary` is the same value, so
                // the button takes the outline rung the strip tiles and the
                // create button already stand on.
                <Button
                  onClick={() => setCustomizeOpen(true)}
                  shape="square"
                  {...(isCompact
                    ? { variant: "outline" as const }
                    : { color: "neutral" as const })}
                />
              }
            >
              <Icon name="settings-2" size="sm" />
              <Text truncate>Customize Sidebar</Text>
            </Box>
          </SidebarPaneFooter>
          <CustomizeNavigationDialog
            initialTab={customizeNav.initialTab}
            onOpenChange={setCustomizeOpen}
            open={customizeOpen}
          />
        </>
      )}
    </SidebarPane>
  );
}
